import {
    read_same_seller_identity,
    read_seller_identity_key,
    read_seller_identity_keys,
    read_seller_member,
    read_seller_name,
    read_seller_payload,
    read_seller_select_value
} from "../../../../utils/hr/seller_identity";
import { read_quest_provider_counts } from "../../../../utils/quest/quest_provider_counts";

export const pageSize = 50;
export const prospectLeadBacklogStatus = "Backlog";
export const prospectLeadAssignedStatus = "To Call";
export const prospectLeadInProgressStatus = "In Progress";
export const prospectLeadStallingStatus = "Stalling";
export const prospectLeadLegacyContractSentStatus = "Contract Sent";
export const prospectLeadAgencyStatus = "Agency";
export const prospectLeadContractSentStatus = prospectLeadAgencyStatus;
export const prospectLeadLostStatus = "Lost";
export const prospectLeadSassStatus = "Sass";
export const prospectLeadProgressActionNotAnswered = "not_answered";
export const prospectLeadProgressActionReschedule = "reschedule";
export const prospectLeadProgressActionCreateOffer = "create_offer";
export const prospectLeadProgressActionSass = "sass";
export const prospectLeadProgressActionNotInterested = "not_interested";
export const prospectLeadProgressActionUpdate = "update";

/**
 * Reads the active ads warning and whether it exhausts both SASS sale options.
 * A single active provider is informational because the other provider remains sellable.
 */
export const read_sass_active_customer_state = (lead = {}) => {
    if (lead?.questCounts?.estimated === true) {
        return { message: "", blocked: false };
    }

    const providerCounts = read_quest_provider_counts(lead).byProvider || {};
    const hasActiveMeta = Number(providerCounts?.meta?.active || 0) > 0;
    const hasActiveGoogle = Number(providerCounts?.google?.active || 0) > 0;

    if (hasActiveMeta && hasActiveGoogle) {
        return { message: "Already a Meta and Google ads customer", blocked: true };
    }

    if (hasActiveMeta) {
        return { message: "Already a Meta ads customer", blocked: false };
    }

    if (hasActiveGoogle) {
        return { message: "Already a Google ads customer", blocked: false };
    }

    return { message: "", blocked: false };
};

const salesClockWeekendFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Athens",
    weekday: "short"
});

export const prospectLeadStatuses = [
    { value: prospectLeadBacklogStatus, label: "BACKLOG" },
    { value: prospectLeadAssignedStatus, label: "TO CALL" },
    { value: prospectLeadInProgressStatus, label: "IN PROGRESS" },
    { value: prospectLeadStallingStatus, label: "STALLING" },
    { value: prospectLeadAgencyStatus, label: "AGENCY" },
    { value: prospectLeadLostStatus, label: "LOST" }
];

export const prospectLeadBoardStatuses = [
    { value: prospectLeadAssignedStatus, label: "TO CALL" },
    { value: prospectLeadInProgressStatus, label: "IN PROGRESS" },
    { value: prospectLeadStallingStatus, label: "STALLING" }
];

export const prospectLeadProgressActions = [
    { value: prospectLeadProgressActionNotAnswered, label: "TO CALL" },
    { value: prospectLeadProgressActionUpdate, label: "UPDATE" },
    { value: prospectLeadProgressActionReschedule, label: "STALLING" },
    { value: prospectLeadProgressActionCreateOffer, label: "AGENCY" },
    { value: prospectLeadProgressActionSass, label: "SASS" },
    { value: prospectLeadProgressActionNotInterested, label: "LOST" }
];

export const prospectLeadColumnTemplate = prospectLeadBoardStatuses.map(() => "minmax(32rem, 1fr)").join(" ");

/**
 * Reads whether the HR clock should be allowed to lock the Sales board today.
 */
export const read_sales_clock_lock_enabled = (value = new Date(Date.now())) => {
    const salesDay = salesClockWeekendFormatter.format(value);

    return salesDay !== "Sat" && salesDay !== "Sun";
};

/**
 * Normalizes people names, usernames, ids, and email values for filtering.
 */
export const read_normalized_member_value = (value) => {
    return read_seller_identity_key(value);
};

/**
 * Reads the visible team member name used by the board header.
 */
export const read_member_name = (member) => {
    return read_seller_name(member);
};

/**
 * Reads the canonical option value used by seller dropdowns.
 */
export const read_member_select_value = (member) => {
    return read_seller_select_value(member);
};

/**
 * Reads all member identity keys that may be present on a lead.
 */
export const read_member_keys = (member) => {
    return read_seller_identity_keys(member);
};

/**
 * Reads a stable React key for one HR member avatar.
 */
export const read_member_render_key = (member, memberIndex) => {
    const memberKeys = read_member_keys(member);

    return memberKeys.join("-") || `member-${memberIndex}`;
};

/**
 * Reads compact initials when no avatar image is available.
 */
export const read_avatar_label = (label) => {
    const cleanLabel = `${label || ""}`.split("@")[0].replace(/[._-]+/g, " ").trim();
    const labelParts = cleanLabel.split(/\s+/).filter(Boolean);
    const firstInitial = `${labelParts[0] || ""}`.charAt(0).toUpperCase();
    const secondInitial = `${labelParts[1] || ""}`.charAt(0).toUpperCase();

    return `${firstInitial}${secondInitial}` || "?";
};

/**
 * Reads the first avatar URL shape supported by HR/user records.
 */
export const read_avatar_url = (member) => {
    return `${member?.avatarUrl || member?.profileImage || member?.image || member?.picture || member?.photo || ""}`.trim();
};

/**
 * Reads unique lead contact values from the old and new lead fields.
 */
export const read_unique_values = (mainValue, firstList, secondList) => {
    const values = [
        mainValue,
        ...(firstList || []),
        ...(secondList || [])
    ].filter(Boolean);
    const uniqueValues = values.filter((value, index, self) => self.indexOf(value) === index);

    return uniqueValues.join(", ");
};

/**
 * Reads the lead name from separate first and last name fields.
 */
export const read_lead_name = (lead) => {
    const name = [
        lead?.firstname || lead?.firstName,
        lead?.lastname || lead?.lastName
    ].filter(Boolean).join(" ");

    return name;
};

/**
 * Reads the last extra-info note submitted through the new-lead form.
 */
export const read_last_note = (lead) => {
    const notes = (lead?.actions || []).filter(action => action?.action === "Note");
    const note = notes.at(-1);

    return note?.comment || "";
};

/**
 * Reads the saved prospect board status with a Backlog fallback.
 */
export const read_prospect_status = (lead) => {
    const status = `${lead?.prospectStatus || ""}`.trim();

    if (status === prospectLeadLegacyContractSentStatus) {
        return prospectLeadAgencyStatus;
    }

    const matchingStatus = prospectLeadStatuses.find((statusItem) => {
        return statusItem.value === status || statusItem.label === status.toUpperCase();
    });

    if (matchingStatus) {
        return matchingStatus.value;
    }

    return prospectLeadBacklogStatus;
};

/**
 * Checks whether a lead is still waiting for first assignment.
 */
export const read_is_backlog_lead = (lead) => {
    return read_prospect_status(lead) === prospectLeadBacklogStatus;
};

/**
 * Checks whether a lead is in the first assigned state.
 */
export const read_is_assigned_lead = (lead) => {
    return read_prospect_status(lead) === prospectLeadAssignedStatus;
};

/**
 * Checks whether the slider should show In Progress outcome actions.
 */
export const read_is_in_progress_lead = (lead) => {
    return read_prospect_status(lead) === prospectLeadInProgressStatus;
};

/**
 * Checks whether the slider should only allow phone editing.
 */
export const read_is_phone_only_lead = (lead) => {
    return read_is_backlog_lead(lead) || read_is_assigned_lead(lead);
};

/**
 * Reads the board status a lead should receive after assignment.
 */
export const read_status_after_assignment = (lead) => {
    const currentStatus = read_prospect_status(lead);
    const statusByCurrentStatus = {
        [prospectLeadBacklogStatus]: prospectLeadAssignedStatus
    };

    return statusByCurrentStatus[currentStatus] || currentStatus;
};

/**
 * Normalizes a Growth member into the seller payload expected by Niffler.
 */
export const read_member_assignment_payload = (member) => {
    return read_seller_payload(member);
};

const millisecondsPerDay = 1000 * 60 * 60 * 24;
const stallingScheduleActionNames = ["New Call", "Meeting Set"];

/**
 * Converts one open-session calendar row into the schedule shape used by Sales cards.
 */
const read_sales_session_schedule_action = (event) => {
    const schedule = Object.prototype.hasOwnProperty.call(event?.data || {}, "schedule")
        ? event.data.schedule : event?.to?.schedule;
    return {
        action: "New Call",
        schedule: schedule || undefined,
        createdAt: event?.changedAt,
        updatedAt: event?.changedAt
    };
};

/**
 * Reads the saved occurrence time used to order competing callback dates.
 */
const read_stalling_schedule_action_time = (action) => {
    const actionTime = new Date(action?.changedAt || action?.updatedAt || action?.createdAt || 0).getTime();

    return Number.isNaN(actionTime) ? 0 : actionTime;
};

/**
 * Reads one valid date object without falling back to unrelated row dates.
 */
const read_valid_date = (value) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
};

/**
 * Reads the seller value from current and legacy Brand history shapes.
 */
const read_sales_assignment_history_seller = (history) => {
    if (history?.to?.seller || history?.seller) {
        return history?.to?.seller || history?.seller;
    }

    if (history?.type === "seller") {
        return history?.to;
    }

    return null;
};

/**
 * Reads the timestamp when the card entered its current Sales session.
 */
const read_lead_sales_assignment_date = (lead) => {
    const savedSalesEnteredAt = read_valid_date(lead?.salesEnteredAt || lead?.brandRecord?.salesEnteredAt);

    if (savedSalesEnteredAt) {
        return savedSalesEnteredAt;
    }

    const sellerHistory = (lead?.histories || []).filter((history) => {
        const hasSeller = Boolean(read_sales_assignment_history_seller(history));

        return ["seller", "legacy_seller"].includes(history?.type) && hasSeller;
    }).filter((history) => {
        return Boolean(read_valid_date(history?.createdAt || history?.updatedAt || history?.changedAt));
    }).sort((firstHistory, secondHistory) => {
        const firstDate = read_valid_date(firstHistory?.createdAt || firstHistory?.updatedAt || firstHistory?.changedAt);
        const secondDate = read_valid_date(secondHistory?.createdAt || secondHistory?.updatedAt || secondHistory?.changedAt);

        return firstDate.getTime() - secondDate.getTime();
    }).at(0);

    return read_valid_date(sellerHistory?.createdAt || sellerHistory?.updatedAt || sellerHistory?.changedAt);
};

/**
 * Reads how many full days a card has been on the Sales board.
 */
export const read_lead_age_days = (lead) => {
    const assignedAt = read_lead_sales_assignment_date(lead);

    if (!assignedAt) {
        return null;
    }

    const ageInDays = Math.floor((Date.now() - assignedAt.getTime()) / millisecondsPerDay);

    return Math.max(ageInDays, 0);
};

/**
 * Reads the card footer label for how long a card has been assigned.
 */
export const read_lead_age_label = (lead) => {
    const ageDays = read_lead_age_days(lead);

    if (ageDays === null) {
        return "N/A";
    }

    return `${ageDays} days`;
};

/**
 * Reads the latest saved Stalling scheduled call action.
 */
export const read_lead_stalling_schedule_action = (lead) => {
    if (read_prospect_status(lead) !== prospectLeadStallingStatus) {
        return null;
    }

    const activeSessionActions = (lead?.activeSalesSession?.events || [])
        .filter((event) => event?.type === "calendar")
        .map(read_sales_session_schedule_action);

    // A current-session callback is authoritative over historical actions.
    const scheduleActions = activeSessionActions.length ? activeSessionActions : (lead?.actions || [])
        .filter((action) => stallingScheduleActionNames.includes(action?.action) && action?.schedule);

    return scheduleActions
        .sort((firstAction, secondAction) => {
            return read_stalling_schedule_action_time(firstAction) - read_stalling_schedule_action_time(secondAction);
        })
        .at(-1);
};

/**
 * Reads the saved Stalling callback date and time from the latest scheduled call.
 */
export const read_lead_stalling_schedule_label = (lead) => {
    const scheduledAction = read_lead_stalling_schedule_action(lead);
    const scheduleDate = new Date(scheduledAction?.schedule);

    if (Number.isNaN(scheduleDate.getTime())) {
        return "";
    }

    const day = scheduleDate.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric"
    });
    const time = scheduleDate.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit"
    });

    return `${day} ${time}`;
};

/**
 * Reads the visual urgency class for a Stalling callback date.
 */
export const read_lead_stalling_schedule_status = (lead) => {
    const scheduledAction = read_lead_stalling_schedule_action(lead);
    const scheduleDate = new Date(scheduledAction?.schedule);

    if (Number.isNaN(scheduleDate.getTime())) {
        return "";
    }

    const todayStart = new Date();
    const scheduleStart = new Date(scheduleDate);

    todayStart.setHours(0, 0, 0, 0);
    scheduleStart.setHours(0, 0, 0, 0);

    const daysUntil = Math.floor((scheduleStart.getTime() - todayStart.getTime()) / millisecondsPerDay);

    if (daysUntil <= 0) {
        return "error";
    }

    if (daysUntil <= 2) {
        return "warning";
    }

    return "success";
};

/**
 * Reads the sortable Stalling callback time so overdue callbacks stay first.
 */
export const read_lead_stalling_schedule_time = (lead) => {
    const scheduledAction = read_lead_stalling_schedule_action(lead);
    const scheduleTime = new Date(scheduledAction?.schedule).getTime();

    if (Number.isNaN(scheduleTime)) {
        return Number.MAX_SAFE_INTEGER;
    }

    return scheduleTime;
};

/**
 * Sorts Stalling cards chronologically by their saved callback schedule.
 */
const sort_stalling_leads_by_schedule = (leads) => {
    return [...leads].sort((firstLead, secondLead) => {
        return read_lead_stalling_schedule_time(firstLead) - read_lead_stalling_schedule_time(secondLead);
    });
};

/**
 * Checks whether a lead is in a terminal sales state.
 */
const read_is_closed_sales_lead = (lead) => {
    const rawStatus = `${lead?.prospectStatus || ""}`.trim();
    const currentStatus = read_prospect_status(lead);

    return [
        rawStatus,
        currentStatus
    ].some((status) => [
        prospectLeadLegacyContractSentStatus,
        prospectLeadLostStatus,
        prospectLeadAgencyStatus,
        prospectLeadSassStatus
    ].includes(status));
};

/**
 * Reads the current assigned seller from the saved brand row first, then legacy fallbacks.
 */
export const read_lead_assignee = (lead) => {
    if (read_is_backlog_lead(lead)) {
        return null;
    }

    if (lead?.brandRecord?.seller) {
        return lead.brandRecord.seller;
    }

    if (lead?.assignee) {
        return lead.assignee;
    }

    if (lead?.sellerId) {
        return lead.sellerId;
    }

    if (lead?.seller) {
        return lead.seller;
    }

    const activeHistory = (lead?.histories || []).find((historyItem) => historyItem?.activeSeller === true);
    const latestHistory = (lead?.histories || []).filter((historyItem) => historyItem?.seller).at(-1);

    if (activeHistory?.seller) {
        return activeHistory.seller;
    }

    if (latestHistory?.seller) {
        return latestHistory.seller;
    }

    return null;
};

/**
 * Reads whether the Brand is explicitly kept on the Sales board.
 */
const read_lead_in_sales = (lead) => {
    if (lead?.inSales === true || lead?.brandRecord?.inSales === true) {
        return true;
    }

    if (lead?.inSales === false || lead?.brandRecord?.inSales === false) {
        return false;
    }

    return Boolean(read_lead_assignee(lead));
};

/**
 * Keeps the Sales board limited to active in-Sales work.
 */
export const read_is_sales_board_visible_lead = (lead) => {
    if (read_is_closed_sales_lead(lead)) {
        return false;
    }

    if (!read_lead_in_sales(lead)) {
        return false;
    }

    return true;
};

/**
 * Reads strict identity keys for one assignee, avoiding first-name-only collisions.
 */
export const read_assignee_identity_keys = (assignee) => {
    return read_seller_identity_keys(assignee);
};

/**
 * Checks whether two assigned people represent the same employee.
 */
export const read_same_assignee_identity = (firstAssignee, secondAssignee) => {
    return read_same_seller_identity(firstAssignee, secondAssignee);
};

/**
 * Checks whether the saved assignee already has enough visible seller data.
 */
const read_has_saved_assignee_payload = (assignee) => {
    return Boolean(
        assignee?.avatarUrl
        || assignee?.firstname
        || assignee?.firstName
        || assignee?.lastname
        || assignee?.lastName
        || assignee?.username
        || assignee?.email
        || assignee?.userId
        || assignee?.user_id
        || assignee?.subId
        || assignee?.subscriptionId
        || assignee?.id
        || assignee?._id
    );
};

/**
 * Finds the HR member that matches the lead assignee.
 */
export const read_lead_assignee_member = (lead, teamMembers) => {
    const assignee = read_lead_assignee(lead);

    if (!assignee) {
        return null;
    }

    const matchingMember = read_seller_member(assignee, teamMembers);

    if (matchingMember) {
        return {
            ...(typeof assignee === "object" && assignee ? assignee : {}),
            ...matchingMember,
            avatarUrl: read_avatar_url(matchingMember) || read_avatar_url(assignee)
        };
    }

    if (read_has_saved_assignee_payload(assignee)) {
        return assignee;
    }

    return assignee;
};

/**
 * Reads the unique members who currently own at least one Sales-board Brand.
 * New backends return the complete cross-page list; older responses fall back to loaded rows.
 */
export const read_sales_board_members = (assignedSellers, leads = []) => {
    const candidates = Array.isArray(assignedSellers)
        ? assignedSellers
        : (leads || []).map((lead) => read_lead_assignee_member(lead, [])).filter(Boolean);
    const seenMemberKeys = new Set();

    return candidates.filter((member) => {
        const memberKeys = read_member_keys(member);

        if (!memberKeys.length || memberKeys.some((memberKey) => seenMemberKeys.has(memberKey))) {
            return false;
        }

        memberKeys.forEach((memberKey) => seenMemberKeys.add(memberKey));
        return true;
    });
};

/**
 * Reads the single normalized source stored on the Prospect lead.
 */
export const read_visible_source_tag_names = (lead) => {
    const sourceName = `${lead?.source || lead?.platformSource || ""}`.trim();

    if (!sourceName || sourceName.toLowerCase() === "new lead") {
        return [];
    }

    return [sourceName];
};

/**
 * Finds the HR member that submitted the lead when saved by the New Lead page.
 */
export const read_lead_submitter_member = (lead, teamMembers) => {
    const submitterKeys = [
        lead?.submittedBy,
        lead?.createdBy,
        ...(lead?.actions || []).map((actionItem) => actionItem?.seller)
    ].map((value) => read_normalized_member_value(value)).filter(Boolean);
    const matchingMember = (teamMembers || []).find((member) => {
        const memberKeys = read_member_keys(member);

        return memberKeys.some((memberKey) => submitterKeys.includes(memberKey));
    });

    return matchingMember || null;
};

/**
 * Reads a source label as the username before the email domain.
 */
const read_source_username = (source) => {
    const sourceValue = `${source || ""}`.trim();

    if (!sourceValue.includes("@")) {
        return sourceValue;
    }

    return sourceValue.split("@")[0].trim();
};

/**
 * Resolves saved first-name sources to the HR username-style label.
 */
const read_source_label = (source, teamMembers) => {
    const sourceValue = read_source_username(source);
    const normalizedSourceValue = read_normalized_member_value(sourceValue);
    const matchedMember = (teamMembers || []).find((member) => {
        const firstName = `${member?.firstname || member?.firstName || ""}`.trim();
        const fullName = [member?.firstname || member?.firstName, member?.lastname || member?.lastName].filter(Boolean).join(" ");

        return normalizedSourceValue === read_normalized_member_value(firstName) || normalizedSourceValue === read_normalized_member_value(fullName);
    });

    if (!matchedMember) {
        return sourceValue;
    }

    return read_source_username(matchedMember?.email || matchedMember?.username) || sourceValue;
};

/**
 * Reads the visible source labels for the card.
 */
export const read_lead_source_labels = (lead, teamMembers) => {
    const sourceTagNames = read_visible_source_tag_names(lead);
    const platformSource = `${lead?.platformSource || ""}`.trim();
    const submitterMember = read_lead_submitter_member(lead, teamMembers);
    const submitterName = `${lead?.submittedByName || read_member_name(submitterMember) || ""}`.trim();

    if (sourceTagNames.length) {
        return sourceTagNames.map((sourceName) => read_source_label(sourceName, teamMembers));
    }

    if (platformSource) {
        return [read_source_label(platformSource, teamMembers)];
    }

    if (submitterName) {
        return [read_source_label(submitterName, teamMembers)];
    }

    return [];
};

/**
 * Filters the currently loaded new-lead results by visible board values.
 */
export const filter_leads = (leads, searchTerm) => {
    const cleanSearchTerm = searchTerm.trim().toLowerCase();

    if (!cleanSearchTerm) {
        return leads;
    }

    return leads.filter((lead) => {
        const searchableLead = [
            read_lead_name(lead),
            read_unique_values(lead?.email, lead?.emailList, lead?.emails),
            read_unique_values(lead?.phone, lead?.phoneList, lead?.phones),
            lead?.brand,
            lead?.category,
            lead?.subcategory,
            lead?.location,
            read_last_note(lead),
            read_prospect_status(lead)
        ].filter(Boolean).join(" ").toLowerCase();

        return searchableLead.includes(cleanSearchTerm);
    });
};

/**
 * Reads only the current assignee keys used by the board avatar filters.
 */
export const read_lead_member_keys = (lead) => {
    const leadKeys = read_assignee_identity_keys(read_lead_assignee(lead));

    return leadKeys;
};

/**
 * Filters leads by selected team avatars when assignment data exists.
 */
export const filter_leads_by_members = (leads, selectedMemberKeys) => {
    if (!selectedMemberKeys.length) {
        return leads;
    }

    return leads.filter((lead) => {
        const leadMemberKeys = read_lead_member_keys(lead);

        return selectedMemberKeys.some((memberKey) => leadMemberKeys.includes(memberKey));
    });
};

/**
 * Groups visible leads by the fixed prospect board columns.
 */
export const read_board_columns = (leads) => {
    return prospectLeadBoardStatuses.map((statusItem) => {
        const statusLeads = leads.filter((lead) => read_prospect_status(lead) === statusItem.value);
        const sortedLeads = statusItem.value === prospectLeadStallingStatus ? sort_stalling_leads_by_schedule(statusLeads) : statusLeads;

        return {
            ...statusItem,
            leads: sortedLeads
        };
    });
};
